import medicationIcon from '@cannect/assets/svg/trust-medication.svg'
import shieldHeartIcon from '@cannect/assets/svg/trust-shield-heart.svg'
import supportAgentIcon from '@cannect/assets/svg/trust-support-agent.svg'
import verifiedIcon from '@cannect/assets/svg/trust-verified.svg'
import { Typography } from '@cannect/new-components/atoms'

/**
 * Os quatro pilares. Ícones vêm dos assets exportados do Figma (Material Symbols) porque só o
 * primeiro tem equivalente claro no lucide — pote de medicamento com cruz, escudo com coração e
 * headset com rosto não têm correspondente fiel na biblioteca.
 */
const TRUST_PILLARS = [
  {
    icon: verifiedIcon,
    title: 'Profissionais Especializados',
    paragraphs: [
      'Os profissionais de saúde conectados ao nosso ecossistema têm acesso a cursos e suporte técnico. Além disso, acompanhamos a experiência dos pacientes para garantir que o melhor cuidado é oferecido.',
      'Porque cada paciente é único!'
    ]
  },
  {
    icon: medicationIcon,
    title: 'Operação farmacêutica especializada',
    paragraphs: [
      'Estrutura dedicada à dispensação de medicamentos que atende todas as normas sanitárias exigidas para produtos nacionais e importados.',
      'Além disso, contamos com farmacêuticos que gerenciam as informações técnicas dos medicamentos e procedimentos de farmacovigilância.'
    ]
  },
  {
    icon: shieldHeartIcon,
    title: 'Rastreabilidade e controle sanitário',
    paragraphs: [
      'Acompanhamento das etapas do pedido dentro das regras sanitárias aplicáveis.',
      'Validação de prescrição e documentação para medicamentos controlados ou sujeitos a controle especial.'
    ]
  },
  {
    icon: supportAgentIcon,
    title: 'Suporte ao longo da jornada',
    paragraphs: [
      'Somos uma empresa de saúde.',
      'Time técnico preparado para dúvidas sobre pedidos, produtos e próximas etapas.',
      'Equipe de saúde para suportar e coordenar seu tratamento, sempre conectado com seu médico.'
    ]
  }
]

/**
 * Seção "Jornada segura e confiável" da home (Figma node 4333:870).
 *
 * Quatro cards de igual altura (355px) sobre fundo `secondary-100/50`, o mesmo do "Como funciona".
 *
 * A revisão de 2026-07-31 centralizou o cabeçalho (node 4333:871), promoveu o antigo parágrafo de
 * apoio a eyebrow, encurtou o H2 para "Jornada segura e confiável" — sem itálico e em 36px, não nos
 * 28px dos outros H2 de seção — e removeu o parágrafo.
 *
 * Uma revisão anterior já havia trocado a paleta (antes `marketplace-200` com cards
 * `marketplace-100`) e expandido a copy de cada card de uma linha para dois ou três parágrafos.
 *
 * Três typos do arquivo foram corrigidos no primeiro e no terceiro card: "supote técnico",
 * "profissionais [...] tem acesso" e "sujeito a controle especial".
 *
 * Os cards do protótipo trazem também um overline ("01 — Cannabis Medicinal"), uma tag
 * ("Consulta + Acompanhamento") e um "Leia mais", todos marcados como ocultos — não construídos,
 * conforme a decisão de não implementar blocos ocultos.
 *
 * Contraste do ícone (`secondary-500` sobre `secondary-100`): 3,12:1 — passa o mínimo de 3:1 para
 * objeto gráfico, e o título carrega o significado. Mantida a cor do arquivo.
 */
export const TrustSection = () => (
  <section aria-labelledby="trust-section-heading" className="w-full bg-secondary-100/50">
    <div className="mx-auto flex w-full max-w-[1230px] flex-col gap-12 px-8 py-16">
      <div className="flex w-full flex-col items-center gap-2 text-center">
        <p className="m-0 text-base leading-[1.6] text-neutral-800">
          Profissionais, operação farmacêutica, tecnologia e suporte atuando de forma integrada
        </p>

        {/* 36px aqui, contra os 28px dos demais H2 de seção: é a medida do arquivo (node 4333:946),
            não um deslize. Por isso o tamanho vem explícito em vez do `type="headingTwo"`. */}
        <Typography.Heading
          id="trust-section-heading"
          level={2}
          weight="regular"
          className="m-0 text-[36px] leading-[49.28px] tracking-[-0.448px] text-neutral-800">
          Jornada segura e confiável
        </Typography.Heading>
      </div>

      <ul className="m-0 grid list-none grid-cols-1 items-stretch gap-5 p-0 sm:grid-cols-2 lg:grid-cols-4">
        {TRUST_PILLARS.map((pillar) => (
          <li
            key={pillar.title}
            className="flex flex-col gap-4 rounded-[16px] bg-neutral-50 px-6 py-8 lg:min-h-[355px]">
            <span className="flex w-fit items-center rounded-[8px] bg-secondary-100/50 p-2">
              <img src={pillar.icon} alt="" aria-hidden className="size-6" />
            </span>

            <div className="flex flex-col gap-4">
              <h3 className="m-0 text-base font-semibold leading-normal text-neutral-800">{pillar.title}</h3>

              <div className="flex flex-col gap-4">
                {pillar.paragraphs.map((paragraph) => (
                  <p key={paragraph} className="m-0 text-xs font-light leading-[17.86px] text-neutral-800">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </section>
)
